import * as Sentry from '@sentry/react';

const RELOADED_CHUNKS_STORAGE_KEY = 'stale_chunk_reloaded';

/** How long after starting a reload beforeSend treats errors as its side effect. A reload that never lands must not mute the page. */
export const STALE_CHUNK_RELOAD_SUPPRESSION_MS = 10_000;

let reloadStartedAt: number | null = null;

type ReloadRecord = { build: string; chunks: string[] };

/**
 * The running build. In production this module is part of a content-hashed chunk, so its URL changes
 * with every deploy. Safari's "Importing a module script failed." names no chunk, so the record is
 * kept per build: a WebView that crosses a second deploy can reload again.
 */
function currentBuild(): string {
  return import.meta.url;
}

/** Vite names the chunk in the error message, e.g. "Failed to fetch dynamically imported module: https://…/assets/p-1.js". */
function chunkUrlFrom(error: unknown): string {
  const message = (error as Error | undefined)?.message ?? '';
  const match = message.match(/(https?:\/\/\S+|\/assets\/\S+)/);

  return match ? match[1] : 'unknown';
}

/**
 * Chunks already reloaded for in this build. A record from another build is stale and starts over.
 * Null when storage is unavailable, so the caller can refuse to reload rather than risk a loop.
 */
function readReloadedChunks(): string[] | null {
  try {
    const record: Partial<ReloadRecord> | null = JSON.parse(
      window.sessionStorage.getItem(RELOADED_CHUNKS_STORAGE_KEY) ?? 'null',
    );

    return record?.build === currentBuild() && Array.isArray(record.chunks) ? record.chunks : [];
  } catch {
    return null;
  }
}

function rememberReloadedChunk(reloaded: string[], chunkUrl: string): boolean {
  try {
    const record: ReloadRecord = { build: currentBuild(), chunks: [...reloaded, chunkUrl] };
    window.sessionStorage.setItem(RELOADED_CHUNKS_STORAGE_KEY, JSON.stringify(record));

    return true;
  } catch {
    return false;
  }
}

/**
 * True for a short window after a stale-chunk reload starts, while the page is going away. Vite resolves the failed import to
 * undefined once the event is default-prevented, so Ionic throws `.iosTransitionAnimation` on undefined
 * before the reload lands (SHOP-WEB-1D / -15). Those errors are a side effect of the recovery.
 */
export function isStaleChunkReloadInProgress(): boolean {
  return reloadStartedAt !== null && Date.now() - reloadStartedAt < STALE_CHUNK_RELOAD_SUPPRESSION_MS;
}

/**
 * `vite:preloadError`: a long-lived session (mostly app WebViews) crossed a deploy and asks for a
 * chunk hash the server no longer has. Reload once per chunk per build in a session to pick up the new build.
 * If the same chunk fails again after that reload, the asset really is missing: the error is
 * reported and Vite rethrows it, instead of reloading forever.
 */
export function handleVitePreloadError(event: Event & { payload?: unknown }): void {
  const chunkUrl = chunkUrlFrom(event.payload);
  const reloaded = readReloadedChunks();

  if (reloaded?.includes(chunkUrl)) {
    console.error('[app] stale_chunk_reload_failed', { chunk_url: chunkUrl });
    Sentry.captureException(event.payload ?? new Error(`Chunk still failing after reload: ${chunkUrl}`), {
      tags: { stale_chunk: 'reload_failed' },
      extra: { chunk_url: chunkUrl },
    });

    return;
  }

  if (reloaded === null || !rememberReloadedChunk(reloaded, chunkUrl)) {
    console.error('[app] stale_chunk_reload_skipped', { chunk_url: chunkUrl, reason: 'session_storage_unavailable' });

    return;
  }

  event.preventDefault();
  reloadStartedAt = Date.now();
  console.info('[app] stale_chunk_reload', { chunk_url: chunkUrl });
  window.location.reload();
}
